import proj4 from 'proj4';

/**
 * EPSG:25833 — UTM zone 33N on GRS80, Kartverket's national grid. Used for all of mainland
 * Norway (well outside zone 33's nominal band too), so the whole app works in these metres.
 */
proj4.defs(
  'EPSG:25833',
  '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
);

export function wgs84ToUtm33(lng: number, lat: number): [number, number] {
  return proj4('EPSG:4326', 'EPSG:25833', [lng, lat]) as [number, number];
}

export function utm33ToWgs84(x: number, y: number): [number, number] {
  return proj4('EPSG:25833', 'EPSG:4326', [x, y]) as [number, number];
}

/**
 * True bearing (degrees clockwise from true north) of the UTM 33 grid-north direction at a
 * point — the grid convergence. A direction with grid bearing `b` has true bearing
 * `b + gridNorthBearing`. Roughly −6° in Romsdalen (7.8°E), +14° in eastern Finnmark.
 * Computed numerically so the sign convention can't be wrong.
 */
export function gridNorthBearing(x: number, y: number): number {
  const [lng0, lat0] = utm33ToWgs84(x, y);
  const [lng1, lat1] = utm33ToWgs84(x, y + 100);
  const φ0 = (lat0 * Math.PI) / 180;
  const φ1 = (lat1 * Math.PI) / 180;
  const Δλ = ((lng1 - lng0) * Math.PI) / 180;
  const bearing = Math.atan2(
    Math.sin(Δλ) * Math.cos(φ1),
    Math.cos(φ0) * Math.sin(φ1) - Math.sin(φ0) * Math.cos(φ1) * Math.cos(Δλ),
  );
  return (bearing * 180) / Math.PI;
}
